import React, { useContext, useEffect } from 'react';
import {
	FormGroup,
	FormControl,
	Input,
	Icon,
	Button,
	ButtonSelect,
	ShopifyResourcePicker,
	Alert,
	Dropdown,
} from 'components';
import styles from 'styles/campaign_settings_first_step.module.scss';
import { BOX_CHECK_ICON, CAMPAIGN_TYPES, CAMPAIGN_TARGETS, BREAK_POINTS } from 'lib/globals';
import { COUNTRIES_LIST } from 'lib/countries';
import classNames from 'lib/classNames';
import ShopContext from 'context/ShopContext';
// import compareVersions from 'compare-versions';

const FirstStep = ({ context }) => {
	const { state: campaign, setItem, validationErrors, validate } = context;
	const shopContext = useContext(ShopContext);
	const {
		state: {
			settings: { multiCurrencyMode },
		},
	} = shopContext;
	const select = (type) => setItem('settings.type', type);
	const killDraftBreakPoint = BREAK_POINTS['kill_draft'](shopContext);

	useEffect(() => {
		if (campaign.settings.type === "bundles" && campaign.target.type === 'all_products') {
			setItem('target.type', "specific_products");
		}
		if (campaign.settings.type !== "bundles" && campaign.target.type !== 'all_products' && !campaign.target.selected.length) {
			setItem('target.type', "all_products");
		}
	}, [campaign.settings.type]);

	// useEffect(() => {
	// 	setItem('target.selected', []);
	// }, [campaign.target.type]);

	return (
		<>
			{!!Object.keys(validationErrors).filter((e) => typeof validationErrors[e] === 'string').length && (
				<Alert type="error" title="Errors">
					<ul className={classNames('list-disc list-inside')}>
						{Object.keys(validationErrors).map((key) => {
							const error = validationErrors[key];
							return Array.isArray(error) ? (
								error.filter((e) => typeof e === 'string').map((k, i) => <li key={i}>{k}</li>)
							) : (
								<li key={key}>{error}</li>
							);
						})}
					</ul>
				</Alert>
			)}
			<FormGroup
				label="Basic Settings"
				info={`Setup the campaign triggers and the type of campaign.
			Tip: Most stores can setup an Add to unlock campaign with a free shipping goal that targets all products in store`}
			>
				<FormControl label="choose a name for the campaign" error={validationErrors.name}>
					<Input autofocus placeholder="Name" BindToContext={[context, 'name']} />
				</FormControl>
				{multiCurrencyMode && !killDraftBreakPoint && (
					<Alert
						{...{
							type: 'warning',
							borderPosition: 'border-l-4',
							iconSize: '2x',
							title: 'Some campaign types are not supported',
							icon: 'exclamation-circle',
							customStyle: {
								icon: 'text-orange-400 self-start mt-1',
							},
						}}
						className="my-6 z-5"
					>
						<p>Shopify native checkout doesn't support all campaign types.</p>
						<div className={classNames('flex space-x-3 pt-3 justify-start')}>
							<a
								className={classNames(
									'text-gray-800 text-sm flex items-center hover:text-orange-500 focus:outline-none focus:outline-shadow font-semibold py-1 px-2 uppercase border bg-orange-200 bg-opacity-50 border-orange-300 rounded'
								)}
								href="https://monsterapps.crisp.help/en/article/reasons-a-campaign-might-not-be-working-jzsza7/"
								target="_blank"
							>
								<Icon icon="info-circle" className="mr-2" size="1x" />
								<span className={classNames('text-xs')}>Learn More</span>
							</a>
						</div>
					</Alert>
				)}
				<FormControl label="select a campaign type" error={validationErrors['settings.type']}>
					<ButtonSelect
						options={[
							{
								value: 'add_to_unlock',
								label: CAMPAIGN_TYPES['add_to_unlock'],
								icon: 'unlock',
								title: "(Free gifts, Free shipping, Discount)",
								info: 'Offer customers a reward when a purchase goal is hit and suggest products to add to cart.',
								link: {href:'https://monsterapps.crisp.help/en/article/add-to-unlock-frequently-bought-together-campaign-free-shipping-free-gift-discount-1x7xy1r/', text: "Watch tutorial"}
							},
							{
								value: 'bundles',
								label: CAMPAIGN_TYPES['bundles'],
								// disabled: ({ state }) => {
								// 	return BREAK_POINTS['kill_draft'](shopContext)
								// 		? state.target.type === 'all_products'
								// 		: !!multiCurrencyMode;
								// },
								icon: 'bars',
								info: 'Offer customers quick discounts on bulk orders.',
								link: {href:'https://monsterapps.crisp.help/en/article/buy-more-save-more-campaign-135cfea/', text: "Watch tutorial"}
							},
							{
								value: 'one_check_up_sell',
								label: CAMPAIGN_TYPES['one_check_up_sell'],
								title: "(Shipping protection, Priority processing, Extended warranty, Gift wrapping)",
								icon: BOX_CHECK_ICON,
								info: 'A quick one-click upsell.',
								link: {href:'https://monsterapps.crisp.help/en/article/one-tick-upsell-campaign-vfswak/', text: "Watch tutorial"}
							},
						]}
						showSeparator={false}
						BindToContext={[context, 'settings.type']}
					/>

					{/* <div style={{ textAlign: 'center' }}>
					<Button
						disabled={disabled}
						text={`Customize ${CAMPAIGN_TYPES[campaign.settings.type]}`}
						icon="sliders-v-square"
						onClick={() => next()}
					/>
				</div> */}
				</FormControl>
				{campaign.settings.type === 'bundles' && (
					<Alert
						{...{
							type: 'warning',
							borderPosition: 'border-l-4',
							iconSize: '2x',
							title: 'Buy more save more - Limitation',
							icon: 'exclamation-circle',
							customStyle: {
								icon: 'text-orange-400 self-start mt-1',
							},
						}}
						className="my-6 z-5"
					>
						<p>Only works on the same exact product / Variant (Not ideal for products with many variants)</p>
					</Alert>
				)}
				<FormControl
					label="choose products you want to trigger this campaign"
					error={validationErrors['target.type']}
					info="This campaign will show up on the products you choose below. you can show it on all products or specific"
				>
					<ButtonSelect
						options={CAMPAIGN_TARGETS}
						shopContext={shopContext}
						BindToContext={[
							context,
							'target.type',
							(newValue) =>
								validate({ target: { ...campaign.target, type: newValue } }, [
									'target.type',
									'target.selected',
								]),
						]}
					/>
				</FormControl>
				{campaign.target.type === 'specific_products' && (
					<FormControl label="Specify the products" error={validationErrors['target.selected']}>
						<ShopifyResourcePicker allowMultiple={20} BindToContext={[context, 'target.selected']}>
							{(trigger) => (
								<Button
									text="Select Products"
									type={
										validationErrors['target.selected'] && !!validationErrors['target.selected'][0]
											? 'danger'
											: 'primary'
									}
									onClick={trigger}
									style={{ width: '100%' }}
								/>
							)}
						</ShopifyResourcePicker>
					</FormControl>
				)}
				{campaign.target.type === 'specific_collections' && (
					<FormControl label="Specify the collections" error={validationErrors['target.selected']}>
						<ShopifyResourcePicker allowMultiple={2} BindToContext={[context, 'target.selected']} type="Collection" >
							{(trigger) => (
								<Button
									text="Select Collections"
									type={
										validationErrors['target.selected'] && !!validationErrors['target.selected'][0]
											? 'danger'
											: 'primary'
									}
									onClick={trigger}
									style={{ width: '100%' }}
								/>
							)}
						</ShopifyResourcePicker>
					</FormControl>
				)}


			</FormGroup>
		</>
	);
};

const Tile = (props) => {
	const { active, type, select, icon, title, description } = props;
	return (
		<div
			className={styles.tile}
			data-selected={active}
			onClick={() => {
				if (!active) select(type);
			}}
		>
			<Icon icon={icon} type={active ? 'primary' : 'secondary'} style={{ marginBottom: '16px' }} size={38} />
			<b>{CAMPAIGN_TYPES[type]}</b>
			<p>{description}</p>
		</div>
	);
};

export default FirstStep;
